import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, XCircle } from 'lucide-react';
import delegationsService from '../services/delegations';
import usersService from '../services/users';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import DataCard from '../components/common/DataCard';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';

const DelegationsPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');
    const [tab, setTab] = useState('all');
    const [delegations, setDelegations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [statusFilter, setStatusFilter] = useState('');

    const [formData, setFormData] = useState({
        grantor_user_id: '',
        grantee_user_id: '',
        delegation_type: 'read_only',
        relationship: '',
        effective_until: '',
    });

    useEffect(() => {
        if (apps?.length) {
            const id = resolveDefaultAppId(apps, searchParams.get('app_id'));
            setAppId(id);
            loadDelegations(id, tab, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    const loadDelegations = async (id, activeTab, page) => {
        setLoading(true);
        setError('');
        try {
            if (activeTab === 'all') {
                const data = await delegationsService.getAll(id || appId, {
                    page,
                    limit: 20,
                    status: statusFilter || undefined,
                });
                setDelegations(data?.results || []);
                setPagination(data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
            } else if (activeTab === 'granted') {
                const data = await delegationsService.getMyDelegations(id || appId);
                setDelegations(data || []);
            } else {
                const data = await delegationsService.getDelegatedToMe(id || appId);
                setDelegations(data || []);
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load delegations.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (newTab) => {
        setTab(newTab);
        loadDelegations(appId, newTab, 1);
    };

    const handleGrant = async () => {
        try {
            await delegationsService.grant({ ...formData, app_id: appId });
            setSuccessMessage('Delegation granted.');
            setShowGrantModal(false);
            setFormData({
                grantor_user_id: '',
                grantee_user_id: '',
                delegation_type: 'read_only',
                relationship: '',
                effective_until: '',
            });
            loadDelegations(appId, tab, 1);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to grant delegation.');
        }
    };

    const handleRevoke = async (delegationId) => {
        if (!window.confirm('Revoke this delegation?')) return;
        try {
            await delegationsService.revoke(delegationId);
            setSuccessMessage('Delegation revoked.');
            loadDelegations(appId, tab, pagination.page);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to revoke.');
        }
    };

    const openGrantModal = async () => {
        setShowGrantModal(true);
        try {
            const users = await usersService.getAllActiveUsersAcrossApps();
            setAllUsers(users || []);
        } catch {
            setAllUsers([]);
        }
    };

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Delegations"
                subtitle="Manage delegated access between users"
                actions={
                    <div className="flex items-center gap-3">
                        <select
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={appId}
                            onChange={(e) => {
                                setAppId(e.target.value);
                                setSelectedAppId(e.target.value);
                                loadDelegations(e.target.value, tab, 1);
                            }}
                        >
                            {apps?.map((a) => (
                                <option key={a.app_id} value={a.app_id}>
                                    {a.app_name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={openGrantModal}
                            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                        >
                            <Plus size={16} /> Grant Delegation
                        </button>
                    </div>
                }
            />

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                {[
                    { key: 'all', label: 'All Delegations' },
                    { key: 'granted', label: 'Granted by Me' },
                    { key: 'received', label: 'Granted to Me' },
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => handleTabChange(t.key)}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            tab === t.key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'all' && (
                <div className="flex items-center gap-2">
                    <select
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            loadDelegations(appId, 'all', 1);
                        }}
                    >
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="revoked">Revoked</option>
                        <option value="expired">Expired</option>
                    </select>
                </div>
            )}

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700">
                    {successMessage}
                </div>
            )}
            {error && (
                <div className="space-y-2">
                    <ErrorState message={error} />
                    <button
                        onClick={() => {
                            setError('');
                            loadDelegations(appId, tab, 1);
                        }}
                        className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                    >
                        Retry
                    </button>
                </div>
            )}

            {loading ? (
                <SkeletonList />
            ) : delegations.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">No delegations found.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {delegations.map((d) => (
                        <DataCard
                            key={d.delegation_id}
                            title={`${d.grantor_first_name || '?'} ${d.grantor_last_name || ''} \u2192 ${
                                d.grantee_first_name || '?'
                            } ${d.grantee_last_name || ''}`}
                            tag={d.status === 'active' ? 'Active' : d.status}
                            meta={[
                                { label: 'Type', value: d.delegation_type },
                                { label: 'Relationship', value: d.relationship || '\u2014' },
                                { label: 'From', value: d.effective_from?.slice(0, 10) },
                                { label: 'Until', value: d.effective_until?.slice(0, 10) || 'Until revoked' },
                            ]}
                            actions={
                                d.status === 'active' ? (
                                    <button
                                        onClick={() => handleRevoke(d.delegation_id)}
                                        className="mt-3 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-700 bg-red-50 border border-red-100 hover:bg-red-100"
                                    >
                                        <XCircle size={14} /> Revoke
                                    </button>
                                ) : null
                            }
                        />
                    ))}
                    {tab === 'all' && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-4">
                            <button
                                disabled={pagination.page <= 1}
                                onClick={() => loadDelegations(appId, 'all', pagination.page - 1)}
                                className="px-3 py-1 text-sm rounded border hover:bg-slate-50 disabled:opacity-40"
                            >
                                Prev
                            </button>
                            <span className="text-sm text-slate-500">
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page >= pagination.totalPages}
                                onClick={() => loadDelegations(appId, 'all', pagination.page + 1)}
                                className="px-3 py-1 text-sm rounded border hover:bg-slate-50 disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Grant Modal */}
            <Modal isOpen={showGrantModal} onClose={() => setShowGrantModal(false)} title="Grant Delegation">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Grantor (who grants access) *
                        </label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={formData.grantor_user_id}
                            onChange={(e) => setFormData({ ...formData, grantor_user_id: e.target.value })}
                        >
                            <option value="">Select user...</option>
                            {allUsers.map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                    {u.first_name} {u.last_name} ({u.email_id})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Grantee (who receives access) *
                        </label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={formData.grantee_user_id}
                            onChange={(e) => setFormData({ ...formData, grantee_user_id: e.target.value })}
                        >
                            <option value="">Select user...</option>
                            {allUsers.map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                    {u.first_name} {u.last_name} ({u.email_id})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Access Type</label>
                            <select
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                value={formData.delegation_type}
                                onChange={(e) => setFormData({ ...formData, delegation_type: e.target.value })}
                            >
                                <option value="read_only">Read Only</option>
                                <option value="full">Full Access</option>
                                <option value="emergency_only">Emergency Only</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Relationship</label>
                            <select
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                value={formData.relationship}
                                onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                            >
                                <option value="">Select...</option>
                                <option value="spouse">Spouse</option>
                                <option value="parent">Parent</option>
                                <option value="child">Child</option>
                                <option value="guardian">Guardian</option>
                                <option value="caregiver">Caregiver</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Expires On (optional)</label>
                        <input
                            type="date"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={formData.effective_until}
                            onChange={(e) => setFormData({ ...formData, effective_until: e.target.value })}
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowGrantModal(false)}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleGrant}
                            disabled={!formData.grantor_user_id || !formData.grantee_user_id}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Grant
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DelegationsPage;
